import type { CommonEvents } from "@common/services/common-events";
import type { TodoService } from "@common/services/todo-service";
import type { SpaceId } from "@common/types/space";
import type {
  GroupKey,
  Todo,
  TodoId,
  TodoIdMap,
  TodoUpdateDeltaIdMap,
} from "@common/types/todo";
import { getTodoHashtagsStr } from "@common/types/todo";
import { shouldPaywallOnCreateTodo } from "@common/use-cases/should-paywall";
import type { UndoAction } from "@common/use-cases/undo";
import { makeUndoAction, reduceUndoStack } from "@common/use-cases/undo";
import TodoGroupUtils, {
  PRESET_GROUP_KEYS,
} from "@common/utils/todo-group-utils";
import type { DisplayMode, FilterFunction } from "@common/utils/todo-utils";
import TodoUtils, {
  checkCleanParentChildRelation,
  mergeLeftIdMaps,
  mergeLeftPartialIdMaps,
  nullFilterFn,
  withoutDeleted,
} from "@common/utils/todo-utils";
import {
  endOfMonth,
  endOfWeek,
  formatDate,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import _ from "lodash";
import {
  action,
  autorun,
  computed,
  makeObservable,
  observable,
  runInAction,
  toJS,
} from "mobx";
import moment from "moment-timezone";
import type SpaceStore from "./space-store";
import { CACHE_STATE_VERSION } from "./space-store";
import type UserStore from "./user-store";
import type { HashtagColor } from "@common/consts/hashtag-colors";
import type { HashtagMeta } from "@web/src/api/model/hashtag-model";
import { DEFAULT_WEEK_START_DAY } from "@common/consts/view-consts";

export interface SharedTodoStoreDependencies {
  userStore: UserStore;
  spaceStore: SpaceStore;
  todoService: TodoService;
  analytics: {
    track: (eventName: string, eventProperties?: Record<string, any>) => void;
  };
  commonEvents: typeof CommonEvents;
  localStorage: {
    setItem: (key: string, value: string) => Promise<void>;
    getItem: (key: string) => Promise<string | null>;
    removeItem: (key: string) => Promise<void>;
  };
}

export const HASHTAG_LAST_USED_KEY = "HASHTAG_LAST_USED";
const MAX_UNDO_STACK_SIZE = 30;
export default class SharedTodoStore<
  T extends SharedTodoStoreDependencies = SharedTodoStoreDependencies,
> {
  @observable
  spaceId: SpaceId;
  @observable
  deps: T;

  @observable
  fullTodoIdMap: TodoIdMap = {}; // includes deleted todos
  @computed
  get todoIdMap(): TodoIdMap {
    return withoutDeleted(this.fullTodoIdMap);
  }

  /**
   * Todos that have been updated/created on the server
   *
   * Used to determine whether or not a todo has been deleted on the client
   * - Set when a todo is loaded from BE
   * - Set when a todo has successfully been pushed to BE
   *
   */
  @observable
  todoIdsOnRemote = new Set<TodoId>();
  /**
   * Todos that have failed to be updated/created on the server
   */
  @observable
  todoUpdateErrors: Record<TodoId, string> = {};

  @computed
  get todosByGroup() {
    return TodoGroupUtils.todoListByGroup(
      this.todoIdMap,
      this.displayMode,
      this.curHashtagFilterFn
    );
  }

  @computed
  get todayTodos() {
    return this.todosByGroup[PRESET_GROUP_KEYS.TODAY] ?? [];
  }

  @observable
  _lastLoadTime: Date | null = null;
  @observable
  hashtagFilters: string[] = [];
  @observable
  hashtagAntiFilters: string[] = [];
  @observable
  displayMode: DisplayMode = "default";
  @observable
  copyHashtag: boolean = false;
  @observable
  undoStack: UndoAction[] = [];
  @observable
  redoStack: UndoAction[] = [];
  @observable
  isLoading = false;

  @observable
  calendarViewCurrentDate: Date = moment(new Date()).startOf("month").toDate();

  @observable
  _lastUsedHashtagDates: { [hashtag: string]: number } = {};

  @observable
  hashtagMetas: HashtagMeta[] = [];

  constructor(spaceId: string, dependencies: T) {
    this.spaceId = spaceId;
    this.deps = dependencies;

    this.deps.todoService.eventEmitter.on(
      "ON_TODO_UPDATE_SUCCESS",
      ({ todoId }) => {
        runInAction(() => this.onUpdateTodoSuccess(todoId));
      }
    );

    this.deps.todoService.eventEmitter.on(
      "ON_TODO_UPDATE_FAILED",
      ({ todoId, error }) => {
        runInAction(() => this.onUpdateTodoError(todoId, error));
      }
    );

    makeObservable(this);

    // Recent hashtags local storage setup
    this.deps.localStorage.getItem(HASHTAG_LAST_USED_KEY).then((value) => {
      if (value) {
        runInAction(() => {
          this._lastUsedHashtagDates = JSON.parse(value);
        });
      }
    });

    autorun(() => {
      this.deps.localStorage.setItem(
        HASHTAG_LAST_USED_KEY,
        JSON.stringify(this._lastUsedHashtagDates)
      );
    });
  }

  @computed // performance: cache expensive calculations
  get parentChildMap() {
    return TodoUtils.parentChildMap(Object.values<Todo>(this.todoIdMap));
  }

  //========================================================================
  //
  //                            LOADER FUNCTIONS
  //
  //========================================================================

  @action
  async loadTodos() {
    // To prevent async race conditions, flush queue when loading
    await this.deps.todoService.dispatchUpdates();

    runInAction(() => {
      this.isLoading = true;
    });

    try {
      this.loadHashtags();

      let todoIdMap = await this.deps.todoService.getTodos(
        this.spaceId,
        this.deps.userStore.timezone
      );
      if (!todoIdMap) {
        return;
      }

      this.setTodoIdMapMerged(todoIdMap);
      runInAction(() =>
        Object.keys(todoIdMap).map((todoId) => this.todoIdsOnRemote.add(todoId))
      );

      // Must be after setTodoIdMapMerged as _lastLoadTime
      // is used in merging strategy
      runInAction(() => (this._lastLoadTime = new Date()));

      if (this.displayMode === "calendar" && this.deps.userStore.isPremium)
        this.loadResolvedTodos(this.calendarViewCurrentDate, true);

      await this.makeUrgentToday();
    } finally {
      setTimeout(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      }, 200);
    }
  }

  @action
  async loadHistoricalTodos() {
    return await this.deps.todoService.getHistoricalTodos(
      this.spaceId,
      this.deps.userStore.timezone
    );
  }

  @action
  async setTodoIdMapMerged(todoIdMap: TodoIdMap) {
    const todoIds = new Set(
      Object.keys(todoIdMap).concat(Object.keys(this.fullTodoIdMap))
    );

    const editBasedMergedTodoIdMap = Array.from(todoIds).reduce(
      (acc, todoId) => {
        const existingTodo = this.fullTodoIdMap[todoId];
        const incomingTodo = todoIdMap[todoId];

        if (!existingTodo) {
          acc[todoId] = incomingTodo!;
          return acc;
        }

        if (!incomingTodo) {
          // It's either:
          // 1. This todo is new, and remote does not have it
          // 2. This todo was deleted on remote

          /**
           * IMPORTANT: this mechanism assumes that api calls resolve in order
           * that they were called. e.g. if we loadTodos, then create a todo,
           * we assume todoIdsOnRemote only updates after loadTodos resolve.
           *
           * If this assumption fails, we may end up deleting a todo that was
           * created after the loadTodos call, which is not what we want (i.e.
           * todoIdsOnRemote will indicate the todo exists, but loadTodos might
           * omit this todo, resulting in its deletion)
           */

          // 1. new todo, remote does not have it
          if (!this.todoIdsOnRemote.has(existingTodo.id)) {
            console.log(
              "[Not Deleting] Prioritising local:",
              todoId,
              existingTodo.lastEditDate,
              this._lastLoadTime?.getTime()
            );
            this.createTodo(existingTodo);
            acc[todoId] = existingTodo;
            return acc;
          }

          // 2. todo existed on server, and hence must've been deleted on remote
          delete acc[todoId];
          // we no longer need to acknowledge, as we've cleaned out the client's
          // understanding of this todo
          this.todoIdsOnRemote.delete(todoId);

          return acc;
        }

        if (existingTodo.lastEditDate > incomingTodo.lastEditDate) {
          console.log("[Merging into Incoming]", existingTodo, incomingTodo);
          // the local data must be persisted
          acc[todoId] = TodoUtils.mergeTodo(incomingTodo, existingTodo);
          return acc;
        }

        // else, incoming todo can persist
        if (incomingTodo.lastEditDate > existingTodo.lastEditDate) {
          console.log("[Merging into Existing]", existingTodo, incomingTodo);
          acc[todoId] = TodoUtils.mergeTodo(existingTodo, incomingTodo);
          return acc;
        }

        acc[todoId] = incomingTodo;

        return acc;
      },
      {} as TodoIdMap
    );

    this.fullTodoIdMap = editBasedMergedTodoIdMap;
  }

  @action
  setTodoIdMapHard(todoIdMap: TodoIdMap) {
    this.fullTodoIdMap = todoIdMap;
    // When setting hard, we assume that we want to overwrite the full
    // client's perception of the remote state.
    this.todoIdsOnRemote = new Set(Object.keys(todoIdMap));
  }

  todoExists(todoId: TodoId, todoIdMap = this.todoIdMap) {
    return (
      todoIdMap[todoId] !== undefined && todoIdMap[todoId]?.deleted === false
    );
  }

  curHashtagFilterFn: FilterFunction = (todo: Todo) => {
    const passFilter =
      this.hashtagFilters.length === 0
        ? true
        : (getTodoHashtagsStr(todo).some((hashtag) => {
            return this.hashtagFilters.includes(hashtag);
          }) ?? false);

    const passAntiFilter =
      !this.hashtagAntiFilters.length || !getTodoHashtagsStr(todo).length
        ? true
        : !getTodoHashtagsStr(todo).some((hashtag) => {
            return this.hashtagAntiFilters.includes(hashtag);
          });

    return {
      passPositiveFilter: passFilter,
      passNegativeFilter: passAntiFilter,
      hasPositiveFilter: this.hashtagFilters.length !== 0,
      hasNegativeFilter: this.hashtagAntiFilters.length !== 0,
    };
  };

  @computed
  get hashtagSpaces() {
    const sortedHashtags = TodoUtils.hashtagsOrdered(
      this.todoIdMap,
      this.hashtagMetas
    );

    // in order to prevent invalid UI states
    // we should include active filters even if
    // they don't exist anymore
    const activeNoLongerExistentHashtags = [
      ...this.hashtagFilters,
      ...this.hashtagAntiFilters,
    ].filter((hashtag) => !sortedHashtags.includes(hashtag));

    const hashtags = [...sortedHashtags, ...activeNoLongerExistentHashtags];

    return hashtags;
  }

  @computed
  get recentHashtags() {
    const hashtags = this.hashtagSpaces;
    // sort by last used
    return [...hashtags].sort((a, b) => {
      const aLastUsed = this._lastUsedHashtagDates[a] ?? 0;
      const bLastUsed = this._lastUsedHashtagDates[b] ?? 0;
      return bLastUsed - aLastUsed;
    });
  }

  @action
  rerenderTodos() {
    runInAction(() => {
      this.fullTodoIdMap = { ...this.fullTodoIdMap };
    });
  }

  //========================================================================
  //
  //                            MUTATING FUNCTIONS
  //
  //========================================================================
  @action
  async updateTodo(
    todoId: TodoId,
    delta: Partial<Todo>,
    { immediate } = { immediate: false }
  ) {
    let deltaIdMap: TodoUpdateDeltaIdMap = {
      [todoId]: {
        ...delta,
        id: todoId,
        lastEditDate: new Date().valueOf(),
      },
    };
    await this.updateTodos(deltaIdMap, true, { immediate });
  }

  @action
  protected async createTodo(todo: Todo, { immediate } = { immediate: false }) {
    this.pushUndoStack(this.fullTodoIdMap, { [todo.id]: todo });
    this.fullTodoIdMap[todo.id] = todo;
    await this.deps.todoService.createTodo(todo, this.spaceId, {
      immediate,
    });
    this.deps.analytics.track("create_todo", {
      group: TodoGroupUtils.groupKeyForDate(todo.date, this.displayMode),
      displayMode: this.displayMode,
    });
  }

  /**
   * Master method to update todos
   * Handles undo stack management too
   */
  @action
  async updateTodos(
    _deltaIdMap: TodoUpdateDeltaIdMap /** update */ | TodoIdMap /** create */,
    undoable = true,
    { immediate } = { immediate: false }
  ) {
    if (!Object.values(_deltaIdMap).length) return; // don't push anything to undo stack

    // update lastEditDate
    const deltaIdMap = Object.entries(_deltaIdMap).reduce(
      (acc, [todoId, todo]) => {
        acc[todoId] = {
          ...todo,
          lastEditDate: new Date().getTime(),
        };
        return acc;
      },
      {} as TodoUpdateDeltaIdMap
    );

    const oldState = this.fullTodoIdMap;
    undoable && this.pushUndoStack(oldState, deltaIdMap);
    this.fullTodoIdMap = mergeLeftIdMaps(this.fullTodoIdMap, deltaIdMap);

    // update api
    const userId = this.deps.userStore.userProfile?.id;
    if (!userId) return; // offline mode
    await this.deps.todoService.updateTodos(deltaIdMap, this.spaceId, {
      immediate,
    });
  }

  async addHashtag(todoId: TodoId, hashtag: string) {
    const todo = this.fullTodoIdMap[todoId];
    if (!todo) return;
    if (getTodoHashtagsStr(todo).includes(hashtag)) return;
    const meta = [
      ...(todo.meta ?? []),
      { metaType: "HASHTAG", value: hashtag },
    ] satisfies Todo["meta"];
    this.updateTodo(todo.id, { meta });

    runInAction(() => {
      this._lastUsedHashtagDates[hashtag] = Date.now();
    });
  }

  async removeHashtag(todoId: TodoId, hashtag: string) {
    const todo = this.fullTodoIdMap[todoId];
    if (!todo) return;
    if (!getTodoHashtagsStr(todo).includes(hashtag)) return;
    const meta = todo.meta?.filter((item) => item.value != hashtag);
    this.updateTodo(todo.id, { meta });
  }

  async toggleHashtag(todoId: TodoId, hashtag: string) {
    const todo = this.fullTodoIdMap[todoId];
    if (!todo) return;
    const hasHashtag = getTodoHashtagsStr(todo).includes(hashtag);
    hasHashtag
      ? this.removeHashtag(todoId, hashtag)
      : this.addHashtag(todoId, hashtag);
  }

  private async onUpdateTodoSuccess(todoId: TodoId) {
    this.todoIdsOnRemote.add(todoId);
    delete this.todoUpdateErrors[todoId];
  }

  private async onUpdateTodoError(todoId: TodoId, error: string) {
    this.todoUpdateErrors[todoId] = error;
  }

  async addSubtask(ofTodo: Todo, groupKey: GroupKey) {
    return this.withSquashedUndos(() => {
      let newTodo = TodoUtils.createTodo({
        value: "",
        owner: this.deps.userStore.userId,
        space: this.spaceId,
      });

      let deltaTodoIdMap = TodoUtils.insertTodosAfter(
        this.todoIdMap,
        [newTodo],
        ofTodo.id,
        ofTodo.id,
        groupKey,
        this.displayMode,
        this.curHashtagFilterFn
      );

      this.createTodo(newTodo);
      this.updateTodos(deltaTodoIdMap);
      return newTodo;
    });
  }

  addTodo(groupKey: GroupKey) {
    if (
      shouldPaywallOnCreateTodo(
        this.fullTodoIdMap,
        this.deps.userStore.isPremium
      )
    ) {
      this.deps.commonEvents.emit("PAYWALL", { from: "add_todo" });
      return;
    }

    return this.withSquashedUndos(() => {
      let newTodo = TodoUtils.createTodo({
        value: "",
        owner: this.deps.userStore.userId,
        space: this.spaceId,
        meta: this.hashtagFilters.map((hashtag) => ({
          metaType: "HASHTAG",
          value: hashtag,
        })),
      });

      const delta = TodoGroupUtils.applyGroupKeyToTodo(
        newTodo,
        groupKey,
        this.displayMode
      );
      newTodo = { ...newTodo, ...delta };

      const currentTodos = this.todosByGroup[groupKey] ?? [];
      const insertBeforeTodo = currentTodos[0];

      // if there are todos in this group, insert as first
      if (insertBeforeTodo) {
        let deltaTodoIdMap = TodoUtils.insertTodosBefore(
          this.todoIdMap,
          [newTodo],
          insertBeforeTodo.id,
          groupKey,
          this.displayMode,
          this.curHashtagFilterFn
        );

        this.createTodo(newTodo);
        this.updateTodos(deltaTodoIdMap);
        return newTodo;
      }

      this.createTodo(newTodo);
      this.updateTodos({ [newTodo.id]: newTodo });
      return newTodo;
    });
  }

  addTodoLast(groupKey: GroupKey) {
    if (
      shouldPaywallOnCreateTodo(
        this.fullTodoIdMap,
        Boolean(this.deps.userStore.isPremium)
      )
    ) {
      this.deps.commonEvents.emit("PAYWALL", { from: "add_todo" });
      return;
    }

    return this.withSquashedUndos(() => {
      let newTodo = TodoUtils.createTodo({
        value: "",
        owner: this.deps.userStore.userId,
        space: this.spaceId,
        meta: this.hashtagFilters.map((hashtag) => ({
          metaType: "HASHTAG",
          value: hashtag,
        })),
      });

      const delta = TodoGroupUtils.applyGroupKeyToTodo(
        newTodo,
        groupKey,
        this.displayMode
      );
      newTodo = { ...newTodo, ...delta };

      const currentTodos = this.todosByGroup[groupKey] ?? [];
      const todoBeforeInsertPoint = currentTodos[currentTodos.length - 1];

      // if there are todos in this group, insert as first
      if (todoBeforeInsertPoint) {
        let deltaTodoIdMap = TodoUtils.insertTodosAfter(
          this.todoIdMap,
          [newTodo],
          null,
          todoBeforeInsertPoint.id,
          groupKey,
          this.displayMode,
          this.curHashtagFilterFn
        );

        this.createTodo(newTodo);
        this.updateTodos(deltaTodoIdMap);
        return newTodo;
      }

      this.createTodo(newTodo);
      this.updateTodos({ [newTodo.id]: newTodo });
      return newTodo;
    });
  }

  async insertTodo(afterTodo: Todo, groupKey: GroupKey) {
    return this.withSquashedUndos(() => {
      let newTodo = TodoUtils.createTodo({
        value: "",
        owner: this.deps.userStore.userId,
        space: this.spaceId,
      });

      let deltaTodoIdMap = TodoUtils.insertTodosAfter(
        this.todoIdMap,
        [newTodo],
        afterTodo.parent,
        afterTodo.id,
        groupKey,
        this.displayMode,
        this.curHashtagFilterFn
      );

      this.createTodo(newTodo);
      this.updateTodos(deltaTodoIdMap);
      return newTodo;
    });
  }

  deleteTodo(todoId: TodoId) {
    const { updatedIdMap } = TodoUtils.softDeleteTodo(this.todoIdMap, todoId);
    this.updateTodos(updatedIdMap);
  }

  /**
   * Soft delete a todo and all its children
   */
  deleteTodoDeep(todoId: TodoId) {
    const { updatedIdMap } = TodoUtils.softDeleteTodoDeep(
      this.todoIdMap,
      todoId
    );
    this.updateTodos(updatedIdMap);
  }

  indentTodo(todoId: TodoId, offset: number, groupKey: GroupKey) {
    let deltaTodoIdMap = TodoUtils.indentTodo(
      this.todoIdMap,
      todoId,
      offset,
      groupKey,
      this.displayMode,
      this.curHashtagFilterFn
    );
    this.updateTodos(deltaTodoIdMap);
  }

  /**
   * Mark complete and check if todo
   * needs to be "deleted"
   * @returns did become complete branch
   */
  markComplete(
    todoId: TodoId,
    completed: boolean,
    moveToTodayOnComplete: boolean
  ) {
    const todoUpdateDelta = TodoUtils.markComplete(
      todoId,
      completed,
      this.todoIdMap,
      moveToTodayOnComplete
    );
    this.updateTodos(todoUpdateDelta);
    this.deps.analytics.track("mark_complete");
  }

  rolloverYstd() {
    const deltaTodoIdMap = TodoUtils.rolloverYstd(this.todoIdMap);
    this.updateTodos(deltaTodoIdMap);
  }

  rolloverBacklog() {
    const deltaTodoIdMap = TodoUtils.rolloverBacklog(this.todoIdMap);
    this.updateTodos(deltaTodoIdMap);
  }

  backlogToSomeday() {
    const deltaTodoIdMap = TodoUtils.backlogToSomeday(
      this.todoIdMap,
      this.displayMode,
      this.curHashtagFilterFn
    );
    this.updateTodos(deltaTodoIdMap);
  }

  reindexIfNeeded() {
    const deltaTodoIdMap = TodoUtils.reindexIfNeeded(
      this.todoIdMap,
      this.displayMode
    );
    this.updateTodos(deltaTodoIdMap, false);
  }

  squashUndoUpdateMap: TodoUpdateDeltaIdMap = {};
  isSquashing = false; // in the middle of a squash undo

  withSquashedUndos<R>(fn: () => R): R {
    const oldTodoIdMap = _.cloneDeep(this.todoIdMap);
    this.squashUndoUpdateMap = {};
    this.isSquashing = true;
    const returnVal = fn();
    const isPromise = returnVal instanceof Promise;
    (isPromise ? returnVal : Promise.resolve(returnVal))
      .then((val) => {
        const action = makeUndoAction(oldTodoIdMap, this.squashUndoUpdateMap);
        this.undoStack.push(action);
        // reduce if needed
        this.undoStack = reduceUndoStack(this.undoStack).slice(
          -MAX_UNDO_STACK_SIZE
        );
        this.redoStack = [];

        return val;
      })
      .finally(() => (this.isSquashing = false));

    return returnVal;
  }

  pushUndoStack(oldTodoIdMap: TodoIdMap, deltaUpdateMap: TodoUpdateDeltaIdMap) {
    if (this.isSquashing) {
      this.squashUndoUpdateMap = mergeLeftPartialIdMaps(
        this.squashUndoUpdateMap,
        deltaUpdateMap
      );
      return;
    }

    const action = makeUndoAction(oldTodoIdMap, deltaUpdateMap);
    this.undoStack.push(action);
    // reduce if needed
    this.undoStack = reduceUndoStack(this.undoStack).slice(
      -MAX_UNDO_STACK_SIZE
    );
    this.redoStack = [];
  }

  undo() {
    const action = this.undoStack.pop();
    if (!action) {
      return;
    }

    this.updateTodos(action.from, false);
    this.redoStack.push(action);
  }

  redo() {
    const action = this.redoStack.pop();
    if (!action) {
      return;
    }
    this.updateTodos(action.to, false);
    this.undoStack.push(action);
  }

  //========================================================================
  //
  //                            UI ACTIONS
  //
  //========================================================================
  setHashtagFilters(filters: string[], antifilters: string[]) {
    runInAction(() => {
      this.hashtagFilters = filters;
      this.hashtagAntiFilters = antifilters;
    });
  }

  addHashtagFilters(filters: string[], antifilters: string[]) {
    for (let filter of filters) {
      // remove from anti filter if exists
      if (this.hashtagAntiFilters.includes(filter)) {
        this.hashtagAntiFilters = this.hashtagAntiFilters.filter(
          (f) => f !== filter
        );
      }

      if (!this.hashtagFilters.includes(filter)) {
        this.hashtagFilters.push(filter);
      }
    }

    for (let filter of antifilters) {
      // remove from anti filter if exists
      if (this.hashtagFilters.includes(filter)) {
        this.hashtagFilters = this.hashtagFilters.filter((f) => f !== filter);
      }

      if (!this.hashtagAntiFilters.includes(filter)) {
        this.hashtagAntiFilters.push(filter);
      }
    }
  }

  removeHashtagFilter(filter: string) {
    this.hashtagFilters = this.hashtagFilters.filter((f) => f !== filter);
    this.hashtagAntiFilters = this.hashtagAntiFilters.filter(
      (f) => f !== filter
    );
  }

  @action
  cleanupParentChildRelations() {
    runInAction(() => {
      const delta = checkCleanParentChildRelation(this.fullTodoIdMap);
      this.fullTodoIdMap = mergeLeftIdMaps(this.fullTodoIdMap, delta);
      Object.keys(delta).length &&
        console.log("[Cleanup] Cleaned up parent child relations", delta);
    });
  }

  private async loadHashtags() {
    const hashtagMetas = await this.deps.todoService.getHashtagsForSpace(
      this.spaceId
    );
    runInAction(() => {
      this.hashtagMetas = hashtagMetas;
    });
  }

  @action
  async deleteHashtagMeta(hashtag: string) {
    if (!this.hashtagMetas.find((h) => h.hashtag === hashtag)) return;

    // Optimistic
    runInAction(() => {
      this.hashtagMetas = this.hashtagMetas.filter(
        (h) => h.hashtag !== hashtag
      );
    });

    await this.deps.todoService.deleteHashtagMeta(hashtag, this.spaceId);
  }

  @action
  async upsertHashtagMetas(hashtagMetas: HashtagMeta[]) {
    runInAction(() => {
      for (const updatedHashtagMeta of hashtagMetas) {
        if (
          !this.hashtagMetas.find(
            (h) => h.hashtag === updatedHashtagMeta.hashtag
          )
        ) {
          this.hashtagMetas.push(updatedHashtagMeta);
          continue;
        }

        this.hashtagMetas = this.hashtagMetas.map((h) =>
          h.hashtag === updatedHashtagMeta.hashtag ? updatedHashtagMeta : h
        );
      }
    });
    await this.deps.todoService.upsertHashtagMetas(hashtagMetas);
  }

  getHashtagColor(hashtag: string): HashtagColor {
    return (
      this.hashtagMetas.find((m) => m.hashtag === hashtag)?.color || "gray"
    );
  }

  @action
  async makeUrgentToday() {
    const allTodosList = Object.values(
      TodoUtils.withoutDeleted(this.fullTodoIdMap)
    );

    const parentChildMapCache = TodoUtils.parentChildMap(allTodosList);

    // for any todo that has urgent tag, find root
    // move all branches to today
    const pastUrgentTodos = allTodosList.filter(
      (t) =>
        t.tag === "urgent" &&
        t.date &&
        moment(t.date).isBefore(moment().startOf("day")) &&
        !TodoUtils.isBranchComplete(
          t.id,
          this.fullTodoIdMap,
          parentChildMapCache
        ) // don't move completed branches
    );

    // find all roots of todos
    const rootTodosToMove = new Set<string>();

    for (let todo of pastUrgentTodos) {
      const root = TodoUtils.rootOfTodo(this.fullTodoIdMap, todo.id);
      if (!root) {
        console.warn("Dangling todo", root);
        continue;
      }

      rootTodosToMove.add(root.id);
    }

    const deltaState = TodoUtils.moveTodosToGroup(
      toJS(this.fullTodoIdMap),
      Array.from(rootTodosToMove),
      PRESET_GROUP_KEYS.TODAY,
      "default",
      nullFilterFn
    );

    await this.updateTodos(deltaState);
  }

  @action
  toggleHashtagFilter(hashtag: string) {
    if (this.hashtagFilters.includes(hashtag)) {
      this.setHashtagFilters(
        this.hashtagFilters.filter((v) => v !== hashtag),
        this.hashtagAntiFilters
      );
      this.addHashtagFilters([], [hashtag]);
    } else if (this.hashtagAntiFilters.includes(hashtag)) {
      this.setHashtagFilters(
        this.hashtagFilters,
        this.hashtagAntiFilters.filter((v) => v !== hashtag)
      );
    } else {
      this.addHashtagFilters([hashtag], []);
    }
  }

  generateCacheState() {
    return {
      version: CACHE_STATE_VERSION,
      fullTodoIdMap: this.fullTodoIdMap,
      todoIdsOnRemote: [...this.todoIdsOnRemote],
      todoUpdateErrors: this.todoUpdateErrors,
    };
  }

  @action
  loadCachedState(
    state: Awaited<ReturnType<SharedTodoStore["generateCacheState"]>>
  ) {
    if (state.version !== CACHE_STATE_VERSION) return; // don't load, not compatible

    this.fullTodoIdMap = state.fullTodoIdMap;
    this.todoIdsOnRemote = new Set(state.todoIdsOnRemote);
    this.todoUpdateErrors = state.todoUpdateErrors;
  }

  @action
  setCalendarViewCurrentDate(date: Date) {
    this.calendarViewCurrentDate = moment(date).startOf("month").toDate();
  }

  private resolvedTodosLastLoadTimeByMonth: Record<string, number> = {};

  @action
  async loadResolvedTodos(
    dateInMonth: Date,
    force = false,
    firstDayInWeek = DEFAULT_WEEK_START_DAY
  ) {
    const lastLoadThreshold = 5 * 60 * 1000;

    if (force) this.resolvedTodosLastLoadTimeByMonth = {}; // clear all caches

    const monthKey = formatDate(dateInMonth, "yyyy-MM");
    const lastLoadTime = this.resolvedTodosLastLoadTimeByMonth[monthKey];
    if (lastLoadTime && Date.now() - lastLoadTime < lastLoadThreshold) return;

    // Loads resolved (completed + deleted) todos for the current month
    const startOfMonthDate = startOfMonth(dateInMonth);
    const endOfMonthDate = endOfMonth(dateInMonth);
    const firstDayOfFirstWeek = startOfWeek(startOfMonthDate, {
      weekStartsOn: firstDayInWeek === "monday" ? 1 : 0,
    });
    const lastDayOfLastWeek = startOfDay(endOfWeek(endOfMonthDate));
    const resolvedTodoIdMap = await this.deps.todoService.getTodosV2(
      this.spaceId,
      { deleted: true, completed: true },
      {
        after: firstDayOfFirstWeek.valueOf(),
        before: lastDayOfLastWeek.valueOf(),
      }
    );

    const todoIdMap = TodoUtils.objectMap(resolvedTodoIdMap, (todo) => ({
      ...todo,
      deleted: false,
    }));

    runInAction(() => {
      this.fullTodoIdMap = mergeLeftIdMaps(this.fullTodoIdMap, todoIdMap);
      for (let todo of Object.values(todoIdMap)) {
        this.todoIdsOnRemote.add(todo.id);
      }
      this.resolvedTodosLastLoadTimeByMonth[monthKey] = Date.now();
    });
  }
}
